// Chat-session state (per-browser chat history), mirroring the old
// frontend/chat_sessions.py — persisted to localStorage so a reload doesn't
// lose history (an improvement over Streamlit's in-memory session_state).

import { useCallback, useEffect, useState } from "react";

import type { ChatSession, DisplayMessage } from "../types";

const STORAGE_KEY = "gridscout_chat_sessions";
const ACTIVE_ID_KEY = "gridscout_active_session_id";
const RADIUS_KEY = "gridscout_radius_km";

function loadSessions(): ChatSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ChatSession[]) : [];
  } catch {
    return [];
  }
}

function loadActiveId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ID_KEY);
  } catch {
    return null;
  }
}

function loadRadius(): number {
  try {
    const raw = localStorage.getItem(RADIUS_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : 100;
  } catch {
    return 100;
  }
}

/**
 * Chat history is per-login, not per-browser: wipe it from storage on
 * logout so a different (or the same) user signing back in starts fresh
 * instead of seeing the previous session's chats.
 */
export function clearStoredChatSessions() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ACTIVE_ID_KEY);
  } catch {
    // ignore
  }
}

export function titleFromMessage(text: string): string {
  const trimmed = text.trim();
  return trimmed.length > 40 ? `${trimmed.slice(0, 40)}…` : trimmed;
}

export function useChatSessions() {
  const [sessions, setSessions] = useState<ChatSession[]>(loadSessions);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(loadActiveId);
  const [radiusKm, setRadiusKm] = useState<number>(loadRadius);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
    } catch {
      // ignore quota errors
    }
  }, [sessions]);

  useEffect(() => {
    try {
      if (activeSessionId) localStorage.setItem(ACTIVE_ID_KEY, activeSessionId);
      else localStorage.removeItem(ACTIVE_ID_KEY);
    } catch {
      // ignore
    }
  }, [activeSessionId]);

  useEffect(() => {
    try {
      localStorage.setItem(RADIUS_KEY, String(radiusKm));
    } catch {
      // ignore
    }
  }, [radiusKm]);

  const createSession = useCallback((): string => {
    const id = `s_${Date.now()}`;
    const newSession: ChatSession = { id, title: "New chat", messages: [] };

    setSessions((prev) => [...prev, newSession]);
    setActiveSessionId(id);

    return id;
  }, []);

  const deleteSession = useCallback(
    (id: string) => {
      setSessions((prev) => prev.filter((s) => s.id !== id));

      setActiveSessionId((current) => {
        if (current !== id) return current;
        const remaining = sessions.filter((s) => s.id !== id);
        return remaining.length > 0 ? remaining[remaining.length - 1].id : null;
      });
    },
    [sessions]
  );

  const activeSession = sessions.find((s) => s.id === activeSessionId) ?? null;

  const updateSessionMessages = useCallback((id: string, updater: (messages: DisplayMessage[]) => DisplayMessage[]) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, messages: updater(s.messages) } : s)));
  }, []);

  const setSessionTitle = useCallback((id: string, title: string) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)));
  }, []);

  const clearAllSessions = useCallback(() => {
    setSessions([]);
    setActiveSessionId(null);
  }, []);

  return {
    sessions,
    activeSessionId,
    activeSession,
    radiusKm,
    setRadiusKm,
    setActiveSessionId,
    createSession,
    deleteSession,
    updateSessionMessages,
    setSessionTitle,
    clearAllSessions,
  };
}
